// Utility functions to handle Firebase-to-Supabase field name compatibility
// This helps the frontend work with both camelCase (Firebase) and snake_case (Supabase) field names

/**
 * Get cloth number from dancer/couple object using Supabase field naming
 * @param {Object} dancer - Dancer or couple object
 * @returns {string} Cloth number
 */
export const getClothNumber = (dancer) => {
  return dancer?.cloth_number ||  // Primary: snake_case from Supabase
         dancer?.clothNumber ||   // Fallback: camelCase from API compatibility
         dancer?.number ||
         dancer?.bib ||
         dancer?.participantNumber ||
         dancer?.participant_number ||
         '';
};

/**
 * Get dancer name from object (supports multiple name field patterns)
 * @param {Object} dancer - Dancer or couple object
 * @returns {string} Formatted dancer name
 */
export const getDancerName = (dancer) => {
  if (!dancer) return '';

  // Try various name fields in order of preference
  return dancer?.name ||
         dancer?.fullName ||
         dancer?.full_name ||
         dancer?.displayName ||
         dancer?.display_name ||
         dancer?.leadName ||
         dancer?.lead_name ||
         dancer?.followName ||
         dancer?.follow_name ||
         dancer?.firstName ||
         dancer?.first_name ||
         dancer?.lastName ||
         dancer?.last_name ||
         `${getClothNumber(dancer)}` || '';
};

/**
 * Get formatted dancer names based on dancer type (solo, couple, team)
 * @param {Object} couple - Couple or dancer object
 * @returns {string} Formatted name(s)
 */
export const formatDancerNames = (couple) => {
  if (!couple) return 'Unknown';

  const dancerType = couple?.dancerType || couple?.dancer_type || 'couple';

  switch (dancerType) {
    case 'solo':
      return getDancerName(couple) || 'Unknown Dancer';

    case 'team':
      const teamMembers = couple?.teamMembers || couple?.team_members || [];
      if (teamMembers.length > 0) {
        return teamMembers.join(' & ');
      }
      return getDancerName(couple) || 'Unknown Team';

    case 'couple':
    default:
      const leadName = couple?.leadName || couple?.lead_name;
      const followName = couple?.followName || couple?.follow_name;

      if (leadName && followName) {
        return `${leadName} & ${followName}`;
      } else if (leadName) {
        return leadName;
      } else if (followName) {
        return followName;
      }

      // Fallback to name field
      if (couple?.name && couple.name.trim() && !couple.name.includes('undefined')) {
        return couple.name.trim();
      }

      return 'Unknown Couple';
  }
};

/**
 * Get club ID from couple object (supports both naming conventions)
 * @param {Object} couple - Couple object
 * @returns {string} Club ID
 */
export const getClubId = (couple) => {
  return couple?.clubId || couple?.club_id || '';
};

/**
 * Get club name from couple object (supports both naming conventions)
 * @param {Object} couple - Couple object
 * @returns {string} Club name
 */
export const getClubName = (couple) => {
  return couple?.clubName || couple?.club_name || '';
};

/**
 * Get competition ID from object (supports both naming conventions)
 * @param {Object} obj - Object with competition ID
 * @returns {string} Competition ID
 */
export const getCompetitionId = (obj) => {
  return obj?.competitionId || obj?.competition_id || '';
};

/**
 * Get dancers list from event object (supports both naming conventions)
 * @param {Object} event - Event object
 * @returns {Array} Array of dancers
 */
export const getDancersList = (event) => {
  if (!event) return [];

  let dancers = [];

  // Try multiple possible field names for dancers array
  if (event?.dancers) {
    if (typeof event.dancers === 'string') {
      // Handle Supabase JSON string format
      try {
        dancers = JSON.parse(event.dancers);
      } catch (error) {
        dancers = [];
      }
    } else if (Array.isArray(event.dancers)) {
      dancers = event.dancers;
    }
  } else if (event?.dancers_list) {
    if (typeof event.dancers_list === 'string') {
      try {
        dancers = JSON.parse(event.dancers_list);
      } catch (error) {
        dancers = [];
      }
    } else if (Array.isArray(event.dancers_list)) {
      dancers = event.dancers_list;
    }
  } else if (event?.participants) {
    if (typeof event.participants === 'string') {
      try {
        dancers = JSON.parse(event.participants);
      } catch (error) {
        dancers = [];
      }
    } else if (Array.isArray(event.participants)) {
      dancers = event.participants;
    }
  } else if (event?.contestants) {
    if (typeof event.contestants === 'string') {
      try {
        dancers = JSON.parse(event.contestants);
      } catch (error) {
        dancers = [];
      }
    } else if (Array.isArray(event.contestants)) {
      dancers = event.contestants;
    }
  } else if (event?.entries) {
    if (typeof event.entries === 'string') {
      try {
        dancers = JSON.parse(event.entries);
      } catch (error) {
        dancers = [];
      }
    } else if (Array.isArray(event.entries)) {
      dancers = event.entries;
    }
  }

  // Ensure we have an array
  dancers = Array.isArray(dancers) ? dancers : [];

  return dancers;
};

/**
 * Sort dancers/couples by cloth number (supports both naming conventions)
 * @param {Array} dancers - Array of dancer/couple objects
 * @returns {Array} Sorted array
 */
export const sortByClothNumber = (dancers) => {
  if (!Array.isArray(dancers)) return [];

  return [...dancers].sort((a, b) => {
    const aNum = parseInt(getClothNumber(a)) || 0;
    const bNum = parseInt(getClothNumber(b)) || 0;
    return aNum - bNum;
  });
};

/**
 * Find dancer by cloth number in array (supports both naming conventions)
 * @param {Array} dancers - Array of dancer objects
 * @param {string} clothNumber - Cloth number to find
 * @returns {Object|null} Found dancer object or null
 */
export const findDancerByClothNumber = (dancers, clothNumber) => {
  if (!Array.isArray(dancers) || !clothNumber) return null;

  return dancers.find(dancer => {
    const dancerClothNumber = getClothNumber(dancer);
    return dancerClothNumber === clothNumber;
  });
};

/**
 * Get couple/dancer ID from object (supports both naming conventions)
 * @param {Object} obj - Couple or dancer object
 * @returns {string} ID
 */
export const getCoupleId = (obj) => {
  return obj?.id || obj?.couple_id || '';
};

/**
 * Check if dancer is allocated to event (supports both naming conventions)
 * @param {Object} couple - Couple object
 * @param {Array} events - Array of event objects
 * @returns {boolean} True if allocated to any event
 */
export const isDancerAllocatedToEvent = (couple, events) => {
  if (!Array.isArray(events) || !couple) return false;

  const clothNumber = getClothNumber(couple);

  return events.some(event => {
    if (!event) return false;

    // Use getDancersList to properly handle both JSON strings and arrays
    const dancers = getDancersList(event);
    if (!Array.isArray(dancers)) return false;

    return findDancerByClothNumber(dancers, clothNumber) !== null;
  });
};

/**
 * Get allocated event name for a dancer (supports both naming conventions)
 * @param {Object} couple - Couple object
 * @param {Array} events - Array of event objects
 * @returns {string} Event name or 'No event'
 */
export const getAllocatedEventName = (couple, events) => {
  if (!Array.isArray(events) || !couple) return 'No event';

  const clothNumber = getClothNumber(couple);
  const allocatedEvents = events.filter(event => {
    if (!event) return false;

    // Use getDancersList to properly handle both JSON strings and arrays
    const dancers = getDancersList(event);
    if (!Array.isArray(dancers)) return false;

    return findDancerByClothNumber(dancers, clothNumber) !== null;
  });

  return allocatedEvents.length > 0 ? allocatedEvents[0].name : 'No event';
};

/**
 * Create a helper function to normalize dancer object for consistent field access
 * @param {Object} dancer - Raw dancer object
 * @returns {Object} Normalized dancer object with consistent field names
 */
export const normalizeDancer = (dancer) => {
  if (!dancer) return null;

  // Try multiple possible ID field names in order of preference
  const dancerId = dancer.id ||
                   dancer.couple_id ||
                   dancer.coupleId ||
                   dancer.coupleid ||
                   dancer.dancer_id ||
                   dancer.dancerId ||
                   dancer.participant_id ||
                   dancer.participantId ||
                   dancer._id; // Fallback to MongoDB-style ID

  return {
    // Use camelCase for consistency, but fall back to snake_case
    id: dancerId,
    clothNumber: dancer.clothNumber || dancer.cloth_number || dancer.number || dancer.cloth,
    leadName: dancer.leadName || dancer.lead_name,
    followName: dancer.followName || dancer.follow_name,
    dancerType: dancer.dancerType || dancer.dancer_type,
    teamMembers: dancer.teamMembers || dancer.team_members,
    clubId: dancer.clubId || dancer.club_id,
    clubName: dancer.clubName || dancer.club_name,
    competitionId: dancer.competitionId || dancer.competition_id,
    country: dancer.country,
    name: dancer.name,
    // Include check-in fields - support both camelCase and snake_case
    registrationCheckin: dancer.registrationCheckin !== undefined ? dancer.registrationCheckin :
                         dancer.registration_checkin !== undefined ? dancer.registration_checkin :
                         dancer.registrationCheckin, // fallback to original
    marshallCheckin: dancer.marshallCheckin !== undefined ? dancer.marshallCheckin :
                     dancer.marshall_checkin !== undefined ? dancer.marshall_checkin :
                     dancer.marshallCheckin, // fallback to original
    // Keep original fields for debugging
    _original: dancer
  };
};

/**
 * Create a helper function to normalize event object for consistent field access
 * @param {Object} event - Raw event object
 * @returns {Object} Normalized event object with consistent field names
 */
export const normalizeEvent = (event) => {
  if (!event) return null;

  return {
    // Use camelCase for consistency, but fall back to snake_case
    id: event.id || event.event_id,
    name: event.name,
    // Handle the dancers field properly - parse JSON string if needed
    dancers: getDancersList(event),
    competitionId: event.competitionId || event.competition_id,
    // Map other Supabase fields to camelCase
    itemNumber: event.itemNumber || event.item_number,
    roundType: event.roundType || event.round_type,
    ageGroup: event.ageGroup || event.age_group,
    danceStyle: event.danceStyle || event.dance_style,
    dancerType: event.dancerType || event.dancer_type,
    numHeats: event.numHeats || event.num_heats,
    level: event.level,
    section: event.section,
    active: event.active,
    // Include broadcast state for filtering in Screen Management
    broadcast_state: event.broadcast_state,
    // Keep original fields for debugging
    _original: event
  };
};

/**
 * Create a helper function to format event data for API submission
 * This ensures we send the correct field names to the Supabase backend
 * @param {Object} event - Normalized event object
 * @returns {Object} Event data formatted for API submission
 */
export const formatEventForAPI = (event) => {
  if (!event) return null;

  // Start with the original event data if available
  const apiData = event._original ? { ...event._original } : { ...event };

  // Ensure the ID field is properly set for API calls
  // Use the original ID if available, otherwise use the normalized ID
  if (event.id) {
    apiData.id = event.id;
    // Also include event_id for snake_case compatibility
    apiData.event_id = event.id;
  }

  // Always send both field names for maximum compatibility
  // The Supabase backend will use whichever one it expects
  if (event.dancers) {
    apiData.dancers = event.dancers;
    apiData.dancers_list = event.dancers;
  }

  // Handle other common field mappings
  if (event.competitionId) {
    apiData.competitionId = event.competitionId;
    apiData.competition_id = event.competitionId;
  }

  return apiData;
};

/**
 * Get club field with snake_case to camelCase compatibility
 * @param {Object} club - Club object
 * @returns {string} Field value
 */
export const getClubField = (club, camelCaseField, snakeCaseField) => {
  return club?.[camelCaseField] || club?.[snakeCaseField] || '';
};

/**
 * Get club contact person (supports both naming conventions)
 * @param {Object} club - Club object
 * @returns {string} Contact person
 */
export const getClubContactPerson = (club) => {
  return getClubField(club, 'contactPerson', 'contact_person');
};

/**
 * Get club contact number (supports both naming conventions)
 * @param {Object} club - Club object
 * @returns {string} Contact number
 */
export const getClubContactNumber = (club) => {
  return getClubField(club, 'contactNumber', 'contact_number');
};

/**
 * Get club competition ID (supports both naming conventions)
 * @param {Object} club - Club object
 * @returns {string} Competition ID
 */
export const getClubCompetitionId = (club) => {
  return getClubField(club, 'competitionId', 'competition_id');
};

/**
 * Normalize club object for consistent field access
 * @param {Object} club - Raw club object
 * @returns {Object} Normalized club object with consistent field names
 */
export const normalizeClub = (club) => {
  if (!club) return null;

  return {
    // Use camelCase for consistency, but fall back to snake_case
    id: club.id || club.club_id,
    name: club.name,
    contactPerson: getClubContactPerson(club),
    contactNumber: getClubContactNumber(club),
    competitionId: getClubCompetitionId(club),
    province: club.province,
    country: club.country,
    company: club.company,
    // Keep original fields for debugging
    _original: club
  };
};

/**
 * Get dancer field with snake_case to camelCase compatibility
 * @param {Object} dancer - Dancer object
 * @param {string} camelCaseField - CamelCase field name
 * @param {string} snakeCaseField - Snake_case field name
 * @returns {any} Field value
 */
export const getDancerField = (dancer, camelCaseField, snakeCaseField) => {
  return dancer?.[camelCaseField] ?? dancer?.[snakeCaseField];
};


/**
 * Format dancer data for API submission to Supabase
 * @param {Object} dancer - Normalized dancer object
 * @returns {Object} Dancer data formatted for API submission
 */
export const formatDancerForAPI = (dancer) => {
  if (!dancer) return null;

  // Start with the original dancer data if available
  const apiData = dancer._original ? { ...dancer._original } : { ...dancer };

  // Use Supabase snake_case field names for flokraft_scrutiny_couples table
  if (dancer.leadName) {
    apiData.lead_name = dancer.leadName;
  }

  if (dancer.followName) {
    apiData.follow_name = dancer.followName;
  }

  if (dancer.dancerType) {
    apiData.dancer_type = dancer.dancerType;
  }

  if (dancer.teamMembers) {
    apiData.team_members = dancer.teamMembers;
  }

  if (dancer.clubId) {
    apiData.club_id = dancer.clubId;
  }

  if (dancer.clubName) {
    apiData.club_name = dancer.clubName;
  }

  if (dancer.competitionId) {
    apiData.competition_id = dancer.competitionId;
  }

  // API uses camelCase for check-in fields (as verified by testing)
  if (dancer.registrationCheckin !== undefined) {
    apiData.registrationCheckin = dancer.registrationCheckin;
  }

  if (dancer.marshallCheckin !== undefined) {
    apiData.marshallCheckin = dancer.marshallCheckin;
  }

  // API uses camelCase for cloth number (as verified by testing)
  if (dancer.clothNumber) {
    apiData.clothNumber = dancer.clothNumber;
  }

  // Handle the composed name field (use both camelCase and snake_case for compatibility)
  if (dancer.name) {
    apiData.name = dancer.name;
    apiData.lead_name = dancer.name; // Fallback for lead_name field
  }

  return apiData;
};

/**
 * Format club data for API submission to Supabase
 * @param {Object} club - Normalized club object
 * @returns {Object} Club data formatted for API submission
 */
export const formatClubForAPI = (club) => {
  if (!club) return null;

  // Start with the original club data if available
  const apiData = club._original ? { ...club._original } : { ...club };

  // Always send both field names for maximum compatibility
  // The Supabase backend will use whichever one it expects
  if (club.name) {
    apiData.name = club.name;
  }

  if (club.contactPerson) {
    apiData.contactPerson = club.contactPerson;
    apiData.contact_person = club.contactPerson;
  }

  if (club.contactNumber) {
    apiData.contactNumber = club.contactNumber;
    apiData.contact_number = club.contactNumber;
  }

  if (club.country) {
    apiData.country = club.country;
  }

  if (club.province) {
    apiData.province = club.province;
  }

  if (club.competitionId) {
    apiData.competitionId = club.competitionId;
    apiData.competition_id = club.competitionId;
  }

  return apiData;
};